
import axios from "axios";
import {getPlanetHdEarliestDate} from '../ViewField/ViewLand/Images/planetHdAcquisitionQuery';

const CREATE_LAND_PATH = "/api/v2/fields/:id/lands";
const GET_LAND_PATH = '/api/v1/fields/gis/:id';
const ALL_LANDS_PATH = "/api/v1/fields/gis";
const DATES_PATH = '/api/v1/fields/gis/dates';
const DATES_PATH_BY_LAND = '/api/v1/fields/gis/:id/dates';

const LAYER_PATH = '/api/v1/fields/gis/:id/eo';

const DATES_PATH_V2 = '/api/v2/fields/lands/:id/remote-sensing/dates';
const LAYER_PATH_V2 = "/api/v2/fields/lands/:land_id/remote-sensing/:layer";
const LAYER_TIFF_PATH_V2 = "/api/v2/fields/lands/:land_id/remote-sensing/tiff/:layer"
const UPDATE_LAND_V2 = "/api/v2/fields/lands/:land_id";

const LAYER_POINT_PATH = '/api/v1/fields/gis/:id/eo/point';
const AMBIENT_PATH = '/api/v1/fields/gis/:id/ambient';
const DOWNLOAD_LAYER_PATH = '/api/v1/fields/gis/:id/download';
const DOWNLOAD_AMBIENT_PATH = '/api/v1/fields/gis/:id/ambient/download';
const DOWNLOAD_IMAGE_AMBIENT_PATH = '/api/v1/fields/gis/:id/image-ambient/download';
const DOWNLOAD_AMBIENT_HD_PATH = '/api/v2/fields/lands/:land_id/ambient-hd/download';
const DOWNLOAD_IMAGE_AMBIENT_HD_PATH = '/api/v2/fields/lands/:land_id/image-ambient-hd/download';
const DOWNLOAD_PRESCRIPTION_AMBIENT_PATH = '/api/v1/fields/gis/:id/eo/ambient/prescription/download';
const DOWNLOAD_PRESCRIPTION_IMAGE_AMBIENT_PATH ='/api/v1/fields/gis/:id/eo/image-ambient/prescription/download'; 
const DOWNLOAD_PRESCRIPTION_AMBIENT_HD_PATH ='/api/v2/fields/lands/:land_id/eo/ambient-hd/prescription/download';
const DOWNLOAD_PRESCRIPTION_IMAGE_AMBIENT_HD_PATH ='/api/v2/fields/lands/:land_id/eo/image-ambient-hd/prescription/download';
const DOWNLOAD_PRESCRIPTION_PATH = '/api/v1/fields/gis/:id/eo/prescription/download';
const CLASSIFY_AMBIENT_PATH = "/api/v1/fields/gis/:id/eo/ambient/classify";
const IMAGE_AMBIENT_PATH = "/api/v1/fields/gis/:id/image-ambient";
const HD_ACCESS_PATH = "/api/v2/fields/lands/:land_id/planet-hd/access";
const HD_ACCESS_PREFLIGHT_PATH = "/api/v2/fields/lands/:land_id/planet-hd/access/preflight";
const HD_ACCESS_CONFIRM_PATH = "/api/v2/fields/lands/:land_id/planet-hd/access/confirm";
const HD_ACQUISITIONS_PATH = "/api/v2/fields/lands/:land_id/planet-hd/acquisitions";
const HD_ACQUISITION_PREPARE_PATH = "/api/v2/fields/lands/:land_id/planet-hd/acquisitions/:acquisition_id/prepare";
const HD_ACQUISITION_LAYER_PATH = "/api/v2/fields/lands/:land_id/planet-hd/acquisitions/:acquisition_id/layers/:layer";
const HD_ACQUISITION_SAMPLE_PATH = `${HD_ACQUISITION_LAYER_PATH}/sample`;
const AMBIENT_HD_PATH = "/api/v2/fields/lands/:land_id/ambient-hd";
const IMAGE_AMBIENT_HD_PATH = "/api/v2/fields/lands/:land_id/image-ambient-hd";
const YIELD_MAPS_PATH = "/api/v2/fields/lands/:land_id/yield-maps";
const DOWNLOAD_YIELD_MAP_PATH = "/api/v2/fields/lands/:land_id/yield-maps/:yield_map_id/download";
const SOIL_MAP_PATH = "/api/v2/fields/lands/:land_id/soil-map";
const COPILOT_ANALYSIS_PATH = "/api/v2/fields/lands/:land_id/copilot/analyze";
const REMOTE_SENSING_TIMEOUT_MS = 30000;

const getLocalToday = () => {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${today.getFullYear()}-${month}-${day}`;
};

const getPlanetHdOperationContext = (data = {}) => {
    if (typeof data.operation_context === 'string' && data.operation_context.trim()) {
        return data.operation_context;
    }

    const context = Object.fromEntries(
        Object.entries(data).filter(([key, value]) => (
            value != null && ![
                'ambient_type',
                'date',
                'idempotency_key',
                'expected_company_period_id',
                'image_type',
                'num_classes',
                'operation',
                'operation_context',
            ].includes(key)
        ))
    );

    return Object.keys(context).length > 0 ? JSON.stringify(context) : undefined;
};

const getPlanetHdAccessPayload = (data = {}, {includeIdempotencyKey = false} = {}) => ({
    operation: data.operation || 'view_planet_hd',
    ...(getPlanetHdOperationContext(data)
        ? {operation_context: getPlanetHdOperationContext(data)}
        : {}),
    ...(data.image_type ? {image_type: data.image_type} : {}),
    ...(data.date ? {date: data.date} : {}),
    ...(data.num_classes != null ? {num_classes: data.num_classes} : {}),
    ...(data.ambient_type ? {ambient_type: data.ambient_type} : {}),
    ...(includeIdempotencyKey ? {idempotency_key: data.idempotency_key} : {}),
    ...(includeIdempotencyKey && data.expected_company_period_id
        ? {expected_company_period_id: data.expected_company_period_id}
        : {}),
});

const getPlanetHdAcquisitionQuery = (data = {}) => {
    const legacyCloudPercent = data.cloud_coverage == null
        ? null
        : Number(data.cloud_coverage) / 100;
    const rawCloudFraction = data.cloud_fraction
        ?? data.cloud_fraction_lte
        ?? legacyCloudPercent;
    const cloudFraction = Number(rawCloudFraction);

    return {
        date_from: data.date_from || getPlanetHdEarliestDate(),
        date_to: data.date_to || getLocalToday(),
        ...(rawCloudFraction != null && Number.isFinite(cloudFraction)
            ? {cloud_fraction: Math.min(1, Math.max(0, cloudFraction))}
            : {}),
    };
};

const shouldFallbackToLegacyRemoteSensing = (error) => {
    const status = error?.response?.status;

    return status === 404 || status === 500 || error?.code === 'ECONNABORTED';
};

const isHdRemoteSensingRequest = (options = {}) => (
    options?.quality === 'hd'
    || options?.hd_authorization_id
    || options?.access_id
    || ['hd', 'planet_scope_hd', 'planetscope_hd', 'planetscope', 'planetscope_psscene_sr_8b'].includes(options?.product)
);

const getLegacyLayerName = (layer, options = {}) => {
    if (isHdRemoteSensingRequest(options)) {
        return null;
    }

    if (layer === 'visible') {
        return 'rgb';
    }

    if (layer === 'evi') {
        return options?.enhanced ? 'evi-enhanced' : 'evi-standard';
    }

    if (layer === 'ndvi') {
        return options?.enhanced ? 'ndvi-enhanced' : 'ndvi-standard';
    }

    return null;
};

const getLegacyLayerParams = (layer, options = {}) => {
    const legacyLayer = getLegacyLayerName(layer, options);

    if (!legacyLayer || !options?.date) {
        return null;
    }

    return {
        layer: legacyLayer,
        date: options.date,
    };
};

const normalizeRemoteSensingDates = (payload) => {
    if (Array.isArray(payload)) {
        return payload;
    }

    if (Array.isArray(payload?.dates)) {
        return payload.dates;
    }

    if (Array.isArray(payload?.data?.dates)) {
        return payload.data.dates;
    }

    return [];
};

const getRemoteSensingDatesV2 = (land_id, filters) => {
    return axios.get(DATES_PATH_V2.replace(":id", land_id), {
        params: filters,
        timeout: REMOTE_SENSING_TIMEOUT_MS,
    }).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: normalizeRemoteSensingDates(res.data)
            };
        }
    });
};

const getRemoteSensingDatesLegacy = (land_id) => {
    return axios.get(DATES_PATH_BY_LAND.replace(':id', land_id), {
        timeout: REMOTE_SENSING_TIMEOUT_MS,
    }).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: normalizeRemoteSensingDates(res.data)
            };
        }
    });
};

const getRemoteSensingLayerV2 = (land_id, layer, options, cancelTokenRequest) => {
    return axios.get(LAYER_PATH_V2.replace(':land_id', land_id).replace(':layer', layer), {
        cancelToken: cancelTokenRequest.token,
        params: { ...options },
        responseType: 'blob',
        timeout: REMOTE_SENSING_TIMEOUT_MS,
    }).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data,
                headers: res.headers
            };
        }
    });
};

const getRemoteSensingLayerLegacy = (land_id, legacyParams, cancelTokenRequest) => {
    return axios.get(LAYER_PATH.replace(':id', land_id), {
        cancelToken: cancelTokenRequest.token,
        params: legacyParams,
        responseType: 'blob',
        timeout: REMOTE_SENSING_TIMEOUT_MS,
    }).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data,
                headers: res.headers
            };
        }
    });
};

export const create = (field_id, data) => {
    return axios.post(CREATE_LAND_PATH.replace(':id', field_id), data).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
};

export const update = (land_id, data) => {
    return axios.put(GET_LAND_PATH.replace(':id', land_id), data).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
};

export const updateV2 = (land_id, data) => {
    return axios.put(UPDATE_LAND_V2.replace(':land_id', land_id), data).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
};

export const list = (field_id) => {
    return axios.get("/api/v2/fields/:id/lands".replace(":id", field_id)).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
}

export const deleteLand = (land_id) => {
    return axios.delete(GET_LAND_PATH.replace(':id', land_id)).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
};

export const get = (gis_id) => {
    return axios.get(GET_LAND_PATH.replace(':id', gis_id)).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
};

export const all = (filters) => {
    return axios.get(ALL_LANDS_PATH, { params: { ...filters } }).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
};

export const datesV2 = (land_id, filters) => {
    if (!isHdRemoteSensingRequest(filters)) {
        // The legacy endpoint does not accept cloud filters. Its response cannot
        // distinguish clear from cloudy dates (and may omit catalog dates).
        if (filters?.cloud_coverage_filter === 'below' || filters?.cloud_coverage_filter === 'above') {
            const catalogFilters = {...filters};
            delete catalogFilters.quality;
            return getRemoteSensingDatesV2(land_id, catalogFilters);
        }

        return getRemoteSensingDatesLegacy(land_id).catch(error => {
            if (!shouldFallbackToLegacyRemoteSensing(error)) {
                throw error;
            }

            return getRemoteSensingDatesV2(land_id, filters);
        });
    }

    // PlanetScope is a separate paid product. Returning Sentinel dates here
    // would make the UI appear usable while serving a different source.
    return getRemoteSensingDatesV2(land_id, filters);
}

export const dates = (filters) => {
    return axios.post(DATES_PATH, filters).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
}

export const getLayerV2 = (land_id, layer, options, cancelTokenRequest) => {
    const legacyParams = getLegacyLayerParams(layer, options);

    if (legacyParams) {
        return getRemoteSensingLayerLegacy(land_id, legacyParams, cancelTokenRequest).catch(error => {
            if (!shouldFallbackToLegacyRemoteSensing(error)) {
                throw error;
            }

            return getRemoteSensingLayerV2(land_id, layer, options, cancelTokenRequest);
        });
    }

    return getRemoteSensingLayerV2(land_id, layer, options, cancelTokenRequest).catch(error => {
        const fallbackLegacyParams = getLegacyLayerParams(layer, options);

        if (!fallbackLegacyParams || !shouldFallbackToLegacyRemoteSensing(error)) {
            throw error;
        }

        return getRemoteSensingLayerLegacy(land_id, fallbackLegacyParams, cancelTokenRequest);
    });
}


export const getLayerTiff = (land_id, layer, options, cancelTokenRequest) => {
    return axios.get(LAYER_TIFF_PATH_V2.replace(':land_id', land_id).replace(':layer', layer), {
        cancelToken: cancelTokenRequest.token,
        params: { ...options },
        responseType: 'blob',
        timeout: REMOTE_SENSING_TIMEOUT_MS,
    }).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    }).catch(error => {
        const legacyParams = getLegacyLayerParams(layer, options);

        if (legacyParams && shouldFallbackToLegacyRemoteSensing(error)) {
            return {
                ok: false,
                data: null,
                headers: {},
            };
        }

        throw error;
    });
}

export const getHdAccess = (land_id, options = {}) => {
    return axios.get(HD_ACCESS_PATH.replace(':land_id', land_id), {
        params: getPlanetHdAccessPayload(options),
    }).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
}

export const preflightHdAccess = (land_id, data = {}) => {
    return axios.post(
        HD_ACCESS_PREFLIGHT_PATH.replace(':land_id', land_id),
        getPlanetHdAccessPayload(data)
    ).then(res => {
        if (res.status === 200 || res.status === 201 || res.status === 202) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
}

export const confirmHdAccess = (land_id, data = {}) => {
    return axios.post(
        HD_ACCESS_CONFIRM_PATH.replace(':land_id', land_id),
        getPlanetHdAccessPayload(data, {includeIdempotencyKey: true})
    ).then(res => {
        if (res.status === 200 || res.status === 201 || res.status === 202) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
}

export const searchHdAcquisitions = (land_id, data = {}) => {
    return axios.get(HD_ACQUISITIONS_PATH.replace(':land_id', land_id), {
        params: getPlanetHdAcquisitionQuery(data),
    }).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
}

export const prepareHdAcquisition = (land_id, acquisition_id, data = {}) => {
    return axios.post(
        HD_ACQUISITION_PREPARE_PATH
            .replace(':land_id', land_id)
            .replace(':acquisition_id', acquisition_id),
        {
            idempotency_key: data.idempotency_key,
            ...(data.expected_company_period_id
                ? {expected_company_period_id: data.expected_company_period_id}
                : {}),
        }
    ).then(res => {
        if (res.status === 200 || res.status === 201 || res.status === 202) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
}

export const getHdAcquisitionLayer = (land_id, acquisition_id, layer, options = {}, cancelTokenRequest) => {
    const canonicalLayer = layer === 'rgb' ? 'visible' : layer;
    return axios.get(
        HD_ACQUISITION_LAYER_PATH
            .replace(':land_id', land_id)
            .replace(':acquisition_id', acquisition_id)
            .replace(':layer', canonicalLayer),
        {
            cancelToken: cancelTokenRequest?.token,
            params: {enhanced: options.enhanced === true},
            responseType: 'blob',
            timeout: REMOTE_SENSING_TIMEOUT_MS,
        }
    ).then(res => {
        if (res.status == 200) {
            const contentType = String(
                res.headers?.['content-type'] || res.data?.type || ''
            ).split(';', 1)[0].toLowerCase();

            if (contentType !== 'image/png') {
                const error = new Error('planet-hd-preview-must-be-png');
                error.code = 'PLANET_HD_INVALID_PREVIEW_MEDIA_TYPE';
                throw error;
            }

            return {
                ok: true,
                data: res.data,
                headers: res.headers
            };
        }
    });
}

export const getHdAcquisitionSample = (land_id, acquisition_id, layer, {lng, lat}) => {
    return axios.get(
        HD_ACQUISITION_SAMPLE_PATH
            .replace(':land_id', land_id)
            .replace(':acquisition_id', acquisition_id)
            .replace(':layer', layer),
        {params: {lng, lat}, timeout: REMOTE_SENSING_TIMEOUT_MS}
    ).then(res => ({ok: res.status === 200, data: res.data}));
}

export const listYieldMaps = (land_id) => {
    return axios.get(YIELD_MAPS_PATH.replace(':land_id', land_id)).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
}

export const getSoilMap = (land_id) => {
    return axios.get(SOIL_MAP_PATH.replace(':land_id', land_id)).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
}

export const analyzeWithCopilot = (land_id, data = {}) => {
    return axios.post(
        COPILOT_ANALYSIS_PATH.replace(':land_id', land_id),
        data
    ).then(res => {
        if (res.status === 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
}

export const downloadSoilMap = (land_id, params) => {
    return axios.get(
        SOIL_MAP_PATH.replace(':land_id', land_id),
        { params, responseType: 'blob' }
    ).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
}

export const downloadYieldMap = (land_id, yield_map_id, params) => {
    return axios.get(
        DOWNLOAD_YIELD_MAP_PATH
            .replace(':land_id', land_id)
            .replace(':yield_map_id', yield_map_id),
        { params, responseType: 'blob' }
    ).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
}

export const getLayer = (land_id, options, cancelTokenRequest) => {
    return axios.get(LAYER_PATH.replace(':id', land_id), { cancelToken: cancelTokenRequest.token, params: { ...options }, responseType: 'blob' }).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data,
                headers: res.headers
            };
        }
    });
}

export const getPointValue = (land_id, cancelTokenRequest, options) => {
    return axios.get(LAYER_POINT_PATH.replace(':id', land_id), { cancelToken: cancelTokenRequest, params: { ...options } }).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data,
                headers: res.headers
            };
        }
    });
}

export const getAmbient = (gis_id) => {
    return axios.get(AMBIENT_PATH.replace(':id', gis_id)).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
};

export const getAmbientHd = (land_id) => {
    return axios.get(AMBIENT_HD_PATH.replace(':land_id', land_id)).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
};

export const createAmbient = (gis_id, data) => {
    return axios.post(AMBIENT_PATH.replace(':id', gis_id), data).then(res => {
        if (res.status == 202) {
            return {
                ok: true,
                data: res.data
            };
        }
    });;
};

export const createAmbientHd = (land_id, data) => {
    return axios.post(AMBIENT_HD_PATH.replace(':land_id', land_id), data).then(res => {
        if (res.status == 202) {
            return {
                ok: true,
                data: res.data
            };
        }
    });;
};

export const downloadLayer = (land_id, params) => {
    return axios.get(DOWNLOAD_LAYER_PATH.replace(':id', land_id), { params, responseType: 'blob' }).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
}

export const downloadAmbient = (land_id, params) => {
    return axios.get(DOWNLOAD_AMBIENT_PATH.replace(':id', land_id), { params, responseType: 'blob' }).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
}

export const downloadPrescriptionAmbient = ({ land_id, data }) => {
    return axios.post(DOWNLOAD_PRESCRIPTION_AMBIENT_PATH.replace(':id', land_id), data, { responseType: 'blob' }).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
};

export const downloadPrescription = ({ land_id, data }) => {
    return axios.post(DOWNLOAD_PRESCRIPTION_PATH.replace(':id', land_id), data, { responseType: 'blob' }).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
}

export const deleteAmbient = (land_id) => {
    return axios.delete(AMBIENT_PATH.replace(':id', land_id)).then(res => {
        if (res.status == 200) {
            return {
                ok: true
            };
        }
    });
}

export const deleteAmbientHd = (land_id, data) => {
    return axios.put(AMBIENT_HD_PATH.replace(':land_id', land_id), data).then(res => {
        if (res.status === 200 || res.status === 202) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
}

export const updateAmbient = (gis_id, data) => {
    return axios.put(AMBIENT_PATH.replace(':id', gis_id), data).then(res => {
        if (res.status == 202) {
            return {
                ok: true,
                data: res.data
            };
        }
    });;
};

export const updateAmbientHd = (land_id, data) => {
    return axios.put(AMBIENT_HD_PATH.replace(':land_id', land_id), data).then(res => {
        if (res.status == 202) {
            return {
                ok: true,
                data: res.data
            };
        }
    });;
};

export const classifyPrescription = (land_id, data) => {
    return axios.post(CLASSIFY_AMBIENT_PATH.replace(':id', land_id), data).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            }
        }
    });
};

export const createImageAmbient = (land_id, data) => {
    return axios.post(IMAGE_AMBIENT_PATH.replace(':id', land_id), data).then(res => {
        if (res.status == 202) {
            return {
                ok: true,
                data: res.data
            }
        }
    });
};

export const createImageAmbientHd = (land_id, data) => {
    return axios.post(IMAGE_AMBIENT_HD_PATH.replace(':land_id', land_id), data).then(res => {
        if (res.status === 200 || res.status === 201 || res.status === 202) {
            return {
                ok: true,
                data: res.data
            }
        }
    });
};


export const getImageAmbient = (gis_id) => {
    return axios.get(IMAGE_AMBIENT_PATH.replace(':id', gis_id)).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
};

export const getImageAmbientHd = (land_id) => {
    return axios.get(IMAGE_AMBIENT_HD_PATH.replace(':land_id', land_id)).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
};

export const deleteImageAmbient = (land_id) => {
    return axios.delete(IMAGE_AMBIENT_PATH.replace(':id', land_id)).then(res => {
        if (res.status == 200) {
            return {
                ok: true
            };
        }
    });
}

export const downloadAmbientHd = (land_id, params) => {
    return axios.get(DOWNLOAD_AMBIENT_HD_PATH.replace(':land_id', land_id), { params, responseType: 'blob' }).then(res => {
        if (res.status == 200) {
            return {ok: true, data: res.data};
        }
    });
}

export const downloadImageAmbientHd = (land_id, params) => {
    return axios.get(DOWNLOAD_IMAGE_AMBIENT_HD_PATH.replace(':land_id', land_id), { params, responseType: 'blob' }).then(res => {
        if (res.status == 200) {
            return {ok: true, data: res.data};
        }
    });
}

export const deleteImageAmbientHd = (land_id, data) => {
    return axios.put(IMAGE_AMBIENT_HD_PATH.replace(':land_id', land_id), data).then(res => {
        if (res.status === 200 || res.status === 202) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
}

export const downloadPrescriptionImageAmbient = ({ land_id, data }) => {
    return axios.post(DOWNLOAD_PRESCRIPTION_IMAGE_AMBIENT_PATH.replace(':id', land_id), data, { responseType: 'blob' }).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
};

export const downloadPrescriptionAmbientHd = ({ land_id, data }) => {
    return axios.post(DOWNLOAD_PRESCRIPTION_AMBIENT_HD_PATH.replace(':land_id', land_id), data, { responseType: 'blob' }).then(res => {
        if (res.status == 200) {
            return {ok: true, data: res.data};
        }
    });
};

export const downloadPrescriptionImageAmbientHd = ({ land_id, data }) => {
    return axios.post(DOWNLOAD_PRESCRIPTION_IMAGE_AMBIENT_HD_PATH.replace(':land_id', land_id), data, { responseType: 'blob' }).then(res => {
        if (res.status == 200) {
            return {ok: true, data: res.data};
        }
    });
};

export const downloadImageAmbient = (land_id, params) => {
    return axios.get(DOWNLOAD_IMAGE_AMBIENT_PATH.replace(':id', land_id), { params, responseType: 'blob' }).then(res => {
        if (res.status == 200) {
            return {
                ok: true,
                data: res.data
            };
        }
    });
}
